/**
 * MODAL: CREATE ORIGIN
 * Modal pour créer une nouvelle origine (canal d'acquisition)
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form, FormInput } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';
import type { CreateOriginDto } from '../../types/origin';

const createOriginSchema = z.object({
  name: z
    .string()
    .min(1, 'Le nom est requis')
    .max(50, 'Le nom est trop long')
    .regex(/^[a-z0-9_]+$/, 'Uniquement lettres minuscules, chiffres et underscores'),
  label: z.string().min(1, 'Le libellé est requis').max(100, 'Le libellé est trop long'),
});

type CreateOriginInput = z.infer<typeof createOriginSchema>;

interface CreateOriginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOriginDto) => Promise<void>;
}

export function CreateOriginModal({ isOpen, onClose, onSubmit }: CreateOriginModalProps) {
  const form = useForm<CreateOriginInput>({
    resolver: zodResolver(createOriginSchema),
    defaultValues: {
      name: '',
      label: '',
    },
  });

  function handleClose() {
    form.reset();
    onClose();
  }

  async function handleFormSubmit(data: CreateOriginInput) {
    await onSubmit(data);
    handleClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer une nouvelle origine"
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-origin-form"
        />
      }
    >
      <Form {...form}>
        <form id="create-origin-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="label"
              label="Libellé (affiché aux utilisateurs)"
              placeholder="ex: Google Ads"
              required
            />
            <FormInput
              control={form.control}
              name="name"
              label="Identifiant technique"
              placeholder="ex: google_ads"
              required
            />
            <p className="text-sm text-zinc-500">
              L'identifiant technique doit contenir uniquement des lettres minuscules, des chiffres
              et des underscores.
            </p>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
